import { sql } from 'drizzle-orm';
import { index, integer, pgPolicy, pgTable, timestamp, text, uniqueIndex, uuid, varchar } from 'drizzle-orm/pg-core';
import { organizations } from './organizations';
import { users } from './users';

const tenantPolicy = (name: string) =>
  pgPolicy(name, {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  });

/**
 * One authenticator-app (TOTP) enrollment per user per firm (MIS-265). Keyed by firm so a reset by one
 * firm's owner never weakens the same person's sign-in to another firm.
 * `secretEncrypted` is AES-256-GCM (server/utils/fieldEncryption.ts); the plain secret is never stored or logged.
 */
export const userMfa = pgTable('user_mfa', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  userId: varchar('user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  secretEncrypted: text('secret_encrypted').notNull(),
  status: varchar('status', { length: 16 }).notNull().default('pending'),
  enrolledAt: timestamp('enrolled_at'),
  lastUsedStep: integer('last_used_step'),
  failedAttempts: integer('failed_attempts').notNull().default(0),
  lockedUntil: timestamp('locked_until'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('user_mfa_org_user_unique').on(table.orgId, table.userId),
  tenantPolicy('user_mfa_tenant_isolation'),
]).enableRLS();

/** One-time recovery codes. Only a salted scrypt hash is stored; a used code keeps its row with `usedAt` set. */
export const userMfaRecoveryCodes = pgTable('user_mfa_recovery_codes', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  userId: varchar('user_id').notNull().references(() => users.id, { onDelete: 'cascade' }),
  codeHash: text('code_hash').notNull(),
  usedAt: timestamp('used_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  index('user_mfa_recovery_codes_org_user_idx').on(table.orgId, table.userId),
  tenantPolicy('user_mfa_recovery_codes_tenant_isolation'),
]).enableRLS();

export type UserMfa = typeof userMfa.$inferSelect;
export type UserMfaRecoveryCode = typeof userMfaRecoveryCodes.$inferSelect;
